/*
 * <jj-movie-night-builder> - /pages/movie-night-builder
 *
 * Build Your Own Movie Night. One Add to Event posts a single atomic
 * multi-item /cart/add.js containing:
 *
 *   1. the Basic Movie Night package line, quantity always 1
 *   2. one line per quantity add-on that is above zero
 *      (inflatable couches, mini car mattresses, mini car seat bundles,
 *       blanket bundles, pillow bundles)
 *   3. one line per selected yes/no add-on (the VIP Red Carpet Setup)
 *   4. one line per selected concession machine, at the chosen serving tier
 *
 * Every component stays a real Shopify line at its real Shopify price. Nothing
 * is flattened into a text-only property, so the cart, the Event Details
 * summary, the merchant email and both resource pools all still see the
 * underlying items.
 *
 * THE MARKUP IS THE CONFIGURATION
 * -------------------------------
 * This file holds no product list, no variant id, no price and no total. Every
 * one of those is read from data- attributes that Liquid rendered, so pricing
 * always comes from Shopify. Adding an add-on later is a Liquid change only:
 *
 *   [data-mnb-addon]  a quantity row. Needs data-mnb-variant, data-mnb-price,
 *                     data-mnb-label, data-mnb-property, and optionally
 *                     data-mnb-pool when it draws on a finite physical pool.
 *   [data-mnb-pick]   a yes/no row. Same attributes, no pool, quantity 1.
 *   [data-mnb-machine] a concession card with its serving-tier select.
 *
 * DELIBERATELY SELF CONTAINED
 * ---------------------------
 *   - it does not touch product-form.js, product-info.js, cart.js,
 *     jj-couch-stepper.js or jj-concession-picker.js
 *   - it defines its own custom element and modifies no existing one
 *   - there is no product form on this page, so unlike jj-couch-stepper.js it
 *     never has to intercept a submit event, and window.JJCartExtras is
 *     deliberately NOT used here - that registry exists to let several
 *     elements share ONE intercepted product form, and this page has none
 *
 * SHARED PHYSICAL POOLS
 * ---------------------
 * Pool state arrives as JSON in [data-mnb-pools], rendered from
 * snippets/rental-resource-pool.liquid. Two pools exist today:
 *
 *   couches      10 total   variant: extra-futon
 *   mattresses    6 total   variants: BOTH mini-car-seating variants
 *
 * The mattress pool is the reason Mini Car Mattress and Mini Car Seat Bundle
 * are two variants of one product. Every unit of either option is one physical
 * mattress, so both rows draw on the same six:
 *
 *   rowMax(row) = total - alreadyUsedInCart - includedWithThisPackage
 *                 - the quantity already asked for by the OTHER rows in the
 *                   same pool
 *
 * so 2 mattress-only + 4 bundles is allowed and a third mattress-only on top
 * of 4 bundles cannot be entered at all. A Drive-In package in the cart
 * carries _included_mattresses = 6 and consumes the pool entirely.
 *
 * None of this is enforcement. Like the product-page stepper it is a UX limit;
 * snippets/rental-resource-pool.liquid recomputes authoritatively from the real
 * cart when the Event Request is rendered, and that is what raises the manual
 * review flag.
 *
 * 2026-08-29 (Phase 1) base, couches and concessions.
 * 2026-08-30 mini car seating, VIP red carpet setup and comfort add-ons. The
 * couch stepper became one row in the generic row system rather than a special
 * case; its cap arithmetic, its status line and its reset behaviour are
 * unchanged. Backup of the previous revision:
 * assets/jj-backup-20260830-jj-movie-night-builder.js
 *
 * The preset $60 second-concession add-on is a different pricing system that
 * belongs to /products/movie-night. It is not, and must not be, offered here.
 */

(function () {
  'use strict';

  if (customElements.get('jj-movie-night-builder')) return;

  /* A row with no pool is uncapped in practice; this is only a sanity ceiling. */
  var UNPOOLED_MAX = 99;

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? (typeof fallback === 'number' ? fallback : 0) : parsed;
  }

  function nodes(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /*
   * Unique per add. This is what stops Shopify merging two builds of the same
   * base variant into one line of quantity 2, which would destroy the
   * per-build add-on attribution.
   */
  function makeLineKey() {
    return 'mnb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  customElements.define(
    'jj-movie-night-builder',
    class JJMovieNightBuilder extends HTMLElement {
      connectedCallback() {
        this.baseVariantId = toInt(this.dataset.baseVariant, 0);
        this.basePrice = toInt(this.dataset.basePrice, 0);
        this.baseLabel = this.dataset.baseLabel || 'Movie Night';

        this.includedCouches = toInt(this.dataset.includedCouches, 0);
        this.includedMattresses = toInt(this.dataset.includedMattresses, 0);

        if (!this.baseVariantId) {
          console.error('Movie Night builder: no base variant. Builder disabled.');
          return;
        }

        this.pools = this.readPools();
        this.rows = this.readRows();
        this.picks = this.readPicks();
        this.machines = nodes(this, '[data-mnb-machine]');

        this.summaryList = this.querySelector('[data-mnb-summary-list]');
        this.totalOutput = this.querySelector('[data-mnb-total]');
        this.submitButton = this.querySelector('[data-mnb-submit]');
        this.errorBox = this.querySelector('[data-mnb-error]');
        this.successBox = this.querySelector('[data-mnb-success]');

        this.currency = this.dataset.currency || 'USD';
        this.pending = false;

        try {
          this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currency,
          });
        } catch (error) {
          this.formatter = null;
        }

        this.bindRows();
        this.bindPicks();
        this.bindMachines();

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        }

        this.refresh();
        this.refreshFromCart();
      }

      /* ------------------------------------------------------------ money */

      money(cents) {
        if (this.formatter) return this.formatter.format(cents / 100);
        return '$' + (cents / 100).toFixed(2);
      }

      /* ------------------------------------------------------------ pools */

      readPools() {
        var script = this.querySelector('[data-mnb-pools]');
        if (!script) return {};

        try {
          var parsed = JSON.parse(script.textContent) || {};

          Object.keys(parsed).forEach(function (key) {
            var pool = parsed[key];
            pool.total = toInt(pool.total, 0);
            pool.used = toInt(pool.used, 0);
            pool.included = toInt(pool.included, 0);
            pool.variants = (pool.variants || []).map(function (id) {
              return toInt(id, 0);
            });
          });

          return parsed;
        } catch (error) {
          console.error('Movie Night builder: pool data could not be read, add-ons stay uncapped', error);
          return {};
        }
      }

      /* Units of this pool still free for this build, before the rows spend any. */
      poolAvailable(key) {
        var pool = this.pools[key];
        if (!pool) return UNPOOLED_MAX;
        return Math.max(0, pool.total - pool.used - pool.included);
      }

      /* What the other rows on the same pool have already asked for. */
      poolClaimedByOthers(row) {
        if (!row.pool) return 0;

        var total = 0;
        this.rows.forEach(function (other) {
          if (other === row || other.pool !== row.pool) return;
          total += other.quantity();
        });
        return total;
      }

      /* ------------------------------------------------------------- rows */

      readRows() {
        var self = this;

        return nodes(this, '[data-mnb-addon]').map(function (element) {
          var row = {
            element: element,
            pool: element.dataset.mnbPool || '',
            variantId: toInt(element.dataset.mnbVariant, 0),
            price: toInt(element.dataset.mnbPrice, 0),
            label: element.dataset.mnbLabel || '',
            property: element.dataset.mnbProperty || '',
            input: element.querySelector('[data-mnb-qty]'),
            buttons: nodes(element, '[data-mnb-step]'),
          };

          row.max = function () {
            return Math.max(0, self.poolAvailable(row.pool || null) - self.poolClaimedByOthers(row));
          };

          row.quantity = function () {
            if (!row.input) return 0;
            return Math.max(0, toInt(row.input.value, 0));
          };

          return row;
        }).filter(function (row) {
          return row.variantId > 0 && row.input;
        });
      }

      setRowQuantity(row, next) {
        if (!row.input) return;
        row.input.value = String(Math.min(Math.max(0, next), row.max()));
        this.refresh();
      }

      bindRows() {
        var self = this;

        this.rows.forEach(function (row) {
          row.buttons.forEach(function (button) {
            button.addEventListener('click', function () {
              if (button.disabled) return;
              self.setRowQuantity(row, row.quantity() + toInt(button.dataset.mnbStep, 0));
            });
          });

          row.input.addEventListener('input', function () {
            self.refresh();
          });

          // Typing past the cap is corrected on blur rather than mid-keystroke.
          row.input.addEventListener('blur', function () {
            self.setRowQuantity(row, row.quantity());
          });
        });
      }

      /* ------------------------------------------------------------ picks */

      readPicks() {
        return nodes(this, '[data-mnb-pick]').map(function (element) {
          return {
            element: element,
            variantId: toInt(element.dataset.mnbVariant, 0),
            price: toInt(element.dataset.mnbPrice, 0),
            label: element.dataset.mnbLabel || '',
            property: element.dataset.mnbProperty || '',
            input: element.querySelector('[data-mnb-pick-input]'),
          };
        }).filter(function (pick) {
          return pick.variantId > 0 && pick.input;
        });
      }

      bindPicks() {
        var self = this;

        this.picks.forEach(function (pick) {
          pick.input.addEventListener('change', function () {
            self.refresh();
          });
        });
      }

      /* ------------------------------------------------------- concessions */

      bindMachines() {
        var self = this;

        this.machines.forEach(function (machine) {
          var toggle = machine.querySelector('[data-mnb-machine-toggle]');
          var tierWrap = machine.querySelector('[data-mnb-tier-wrap]');
          var tier = machine.querySelector('[data-mnb-tier]');

          if (!toggle || !tier) return;

          toggle.addEventListener('change', function () {
            var on = toggle.checked;

            // A disabled select is skipped by selectedMachines(), so an
            // unchecked machine can never contribute a cart line.
            tier.disabled = !on;
            if (tierWrap) tierWrap.hidden = !on;

            self.refresh();
          });

          tier.addEventListener('change', function () {
            self.refresh();
          });
        });
      }

      selectedMachines() {
        var chosen = [];

        this.machines.forEach(function (machine) {
          var toggle = machine.querySelector('[data-mnb-machine-toggle]');
          var tier = machine.querySelector('[data-mnb-tier]');

          if (!toggle || !toggle.checked || !tier || tier.disabled) return;

          var option = tier.options[tier.selectedIndex];
          if (!option) return;

          chosen.push({
            id: toInt(option.value, 0),
            quantity: 1,
            unitPrice: toInt(option.dataset.mnbPrice, 0),
            label: toggle.dataset.mnbMachineLabel || '',
            tier: option.dataset.mnbTierLabel || '',
          });
        });

        return chosen.filter(function (item) {
          return item.id > 0;
        });
      }

      /* ---------------------------------------------------------- selection
       * The single source the summary, the total and the cart payload are all
       * built from, so they cannot disagree.
       */
      selection() {
        var items = [
          {
            role: 'base',
            id: this.baseVariantId,
            quantity: 1,
            unitPrice: this.basePrice,
            label: this.baseLabel,
            tier: '',
            property: '',
          },
        ];

        this.rows.forEach(function (row) {
          var quantity = row.quantity();
          if (quantity <= 0) return;

          items.push({
            role: 'addon',
            id: row.variantId,
            quantity: quantity,
            unitPrice: row.price,
            label: row.label,
            tier: '',
            property: row.property,
          });
        });

        this.picks.forEach(function (pick) {
          if (!pick.input.checked) return;

          items.push({
            role: 'addon',
            id: pick.variantId,
            quantity: 1,
            unitPrice: pick.price,
            label: pick.label,
            tier: '',
            property: pick.property,
          });
        });

        this.selectedMachines().forEach(function (machine) {
          items.push({
            role: 'concession',
            id: machine.id,
            quantity: machine.quantity,
            unitPrice: machine.unitPrice,
            label: machine.label,
            tier: machine.tier,
            property: 'Concession for',
          });
        });

        return items;
      }

      /*
       * Recounts both pools from the real cart. Called on load and after every
       * successful add, so a package added on a product page or in another tab
       * is accounted for the next time this page touches the cart.
       */
      refreshFromCart() {
        var self = this;
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            Object.keys(self.pools).forEach(function (key) {
              var pool = self.pools[key];
              var property = '_included_' + key;
              var used = 0;

              (cart.items || []).forEach(function (item) {
                if (pool.variants.indexOf(item.variant_id) !== -1) {
                  used += item.quantity;
                  return;
                }

                var included = item.properties ? item.properties[property] : null;
                if (included) used += toInt(included, 0) * item.quantity;
              });

              pool.used = used;
            });

            // Re-clamp every row against the freshly counted pools.
            self.rows.forEach(function (row) {
              self.setRowQuantity(row, row.quantity());
            });

            self.refresh();
          })
          .catch(function (error) {
            console.error('Movie Night builder: could not read the cart, showing the page-load figures', error);
          });
      }

      /* --------------------------------------------------------- rendering */

      refresh() {
        this.renderRows();
        this.renderPoolStatus();
        this.renderSummary();
      }

      renderRows() {
        var self = this;

        this.rows.forEach(function (row) {
          var max = row.max();
          var quantity = Math.min(row.quantity(), max);

          row.input.max = String(max);

          row.buttons.forEach(function (button) {
            var step = toInt(button.dataset.mnbStep, 0);
            button.disabled = (step > 0 && quantity >= max) || (step < 0 && quantity <= 0);
          });
        });

        return self;
      }

      renderPoolStatus() {
        var self = this;

        nodes(this, '[data-mnb-pool-status]').forEach(function (output) {
          var key = output.dataset.mnbPoolStatus;
          var pool = self.pools[key];
          if (!pool) return;

          var claimed = 0;
          self.rows.forEach(function (row) {
            if (row.pool === key) claimed += row.quantity();
          });

          var remaining = Math.max(0, self.poolAvailable(key) - claimed);

          if (remaining === 0) {
            output.textContent = 'No more available.';
          } else if (remaining === 1) {
            output.textContent = '1 more available.';
          } else {
            output.textContent = remaining + ' more available.';
          }
        });
      }

      renderSummary() {
        var self = this;
        var items = this.selection();
        var total = 0;

        if (this.summaryList) this.summaryList.innerHTML = '';

        items.forEach(function (item) {
          var lineTotal = item.quantity * item.unitPrice;
          total += lineTotal;

          if (!self.summaryList) return;

          var entry = document.createElement('li');
          entry.className = 'jj-mnb__summary-item';

          var left = document.createElement('span');
          left.className = 'jj-mnb__summary-name';
          left.textContent = item.quantity > 1 ? item.quantity + ' × ' + item.label : item.label;

          if (item.tier) {
            var tier = document.createElement('span');
            tier.className = 'jj-mnb__summary-tier';
            tier.textContent = item.tier;
            left.appendChild(tier);
          }

          var price = document.createElement('span');
          price.className = 'jj-mnb__summary-price';
          price.textContent = self.money(lineTotal);

          entry.appendChild(left);
          entry.appendChild(price);
          self.summaryList.appendChild(entry);
        });

        if (this.totalOutput) this.totalOutput.textContent = this.money(total);

        return total;
      }

      /* ------------------------------------------------------------ submit */

      show(box, html) {
        if (!box) return;
        box.innerHTML = html;
        box.hidden = false;
      }

      hide(box) {
        if (!box) return;
        box.hidden = true;
        box.innerHTML = '';
      }

      setPending(isPending) {
        this.pending = isPending;
        var button = this.submitButton;
        if (!button) return;

        button.setAttribute('aria-disabled', isPending ? 'true' : 'false');
        button.classList.toggle('loading', isPending);

        var spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isPending);

        var label = button.querySelector('[data-mnb-submit-label]');
        if (label) label.textContent = isPending ? 'Adding...' : 'Add to Event';
      }

      onSubmit(event) {
        event.preventDefault();

        if (this.pending) return;

        this.hide(this.successBox);
        this.hide(this.errorBox);

        this.addToEvent(this.selection());
      }

      /*
       * One atomic call. Shopify returns a status field only on failure, and
       * because the call is atomic a failure means no line was added at all -
       * so there is never a half-added build to reconcile.
       */
      addToEvent(items) {
        var self = this;
        var root = (window.rentalRoutes && window.rentalRoutes.rootUrl) || '/';
        var addUrl = (window.routes && window.routes.cart_add_url) || root + 'cart/add.js';
        var lineKey = makeLineKey();
        var attribution = 'Build Your Own Movie Night';

        var payload = items.map(function (item) {
          var properties = {};

          if (item.role === 'base') {
            properties._line_key = lineKey;
            properties._included_couches = String(self.includedCouches);
            properties._included_mattresses = String(self.includedMattresses);
            properties['Package type'] = attribution;
          } else {
            properties._package_line_key = lineKey;
            if (item.property) properties[item.property] = attribution;
            if (item.tier) properties['Servings'] = item.tier;
          }

          return { id: item.id, quantity: item.quantity, properties: properties };
        });

        this.setPending(true);

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload }),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              var message =
                (result.data && (result.data.description || result.data.message)) ||
                'We could not add this to your event. Please try again.';
              self.show(self.errorBox, message);
              return null;
            }

            self.onAdded(items);
            return self.refreshCartIcon(root);
          })
          .catch(function (error) {
            console.error('Movie Night builder: add to event failed', error);
            self.show(
              self.errorBox,
              'We could not reach the cart just now. Please check your connection and try again.'
            );
          })
          .then(function () {
            self.setPending(false);
            return self.refreshFromCart();
          });
      }

      onAdded(items) {
        var eventUrl =
          (window.rentalRoutes && window.rentalRoutes.eventDetailsUrl) || '/pages/event-details';

        var summary = items
          .map(function (item) {
            return item.quantity > 1 ? item.quantity + ' x ' + item.label : item.label;
          })
          .join(', ');

        this.show(
          this.successBox,
          'Added to your event: ' +
            summary +
            '. <a href="' +
            eventUrl +
            '">Review your event details</a>'
        );

        // Reset so a second visit starts clean and a double click cannot
        // silently add the same build twice.
        this.rows.forEach(function (row) {
          row.input.value = '0';
        });

        this.picks.forEach(function (pick) {
          pick.input.checked = false;
        });

        this.machines.forEach(function (machine) {
          var toggle = machine.querySelector('[data-mnb-machine-toggle]');
          var tierWrap = machine.querySelector('[data-mnb-tier-wrap]');
          var tier = machine.querySelector('[data-mnb-tier]');

          if (toggle) toggle.checked = false;
          if (tier) {
            tier.disabled = true;
            tier.selectedIndex = 0;
          }
          if (tierWrap) tierWrap.hidden = true;
        });

        this.refresh();
      }

      /*
       * Section Rendering API. The response wraps the section in
       * <div id="shopify-section-cart-icon-bubble">, so the swap source is
       * .shopify-section - #cart-icon-bubble is the <a> in this theme's header
       * and does not appear in the response at all.
       */
      refreshCartIcon(root) {
        return fetch(root + '?sections=cart-icon-bubble')
          .then(function (response) {
            return response ? response.json() : null;
          })
          .then(function (sections) {
            if (!sections || !sections['cart-icon-bubble']) return;

            var parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            var source = parsed.querySelector('.shopify-section');
            var destination = document.querySelector('#cart-icon-bubble');

            if (source && destination) destination.innerHTML = source.innerHTML;

            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jj-movie-night-builder' });
              }
            } catch (error) {
              // The icon has already been re-rendered; the event is a bonus.
            }
          })
          .catch(function (error) {
            console.error('Movie Night builder: cart icon refresh failed', error);
          });
      }
    }
  );
})();
